import React, { useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { ReduxState } from "../../../redux";
import { useTour } from "./TourContext";
import { SUB_TOURS_MENU } from "./tourDefinitions";

const TOOLTIP_WIDTH = 320;
const TOOLTIP_GAP = 12;

// Returns viewport coordinates suitable for `position: fixed`. The app shell
// scrolls inside `.c-body` (CoreUI), not the window, so document-coordinate
// positioning would freeze the tooltip in place when the inner container
// scrolls. Fixed + viewport coords + a scroll listener (in the effect below)
// keeps the tooltip glued to its anchor element.
function getAnchorPosition(el: Element | null, placement: string = "bottom") {
  if (!el) {
    return {
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      centered: true
    };
  }
  const rect = el.getBoundingClientRect();

  switch (placement) {
    case "top":
      return {
        top: rect.top - TOOLTIP_GAP,
        left: rect.left + rect.width / 2,
        transform: "translate(-50%, -100%)",
        centered: false
      };
    case "left":
      return {
        top: rect.top + rect.height / 2,
        left: rect.left - TOOLTIP_GAP,
        transform: "translate(-100%, -50%)",
        centered: false
      };
    case "right":
      return {
        top: rect.top + rect.height / 2,
        left: rect.right + TOOLTIP_GAP,
        transform: "translateY(-50%)",
        centered: false
      };
    default: // bottom
      return {
        top: rect.bottom + TOOLTIP_GAP,
        left: rect.left + rect.width / 2,
        transform: "translateX(-50%)",
        centered: false
      };
  }
}

const TourOverlay: React.FC = () => {
  const { state, tours, nextStep, skipTour, startTour } = useTour();
  const location = useLocation();
  const isSandbox = useSelector((s: ReduxState) => s.Auth.isSandboxMode);
  const [pos, setPos] = useState<any>(null);
  const [visible, setVisible] = useState(false);
  const pollRef = useRef<any>(null);

  const { active, tourId, stepIndex } = state;
  const def = tourId ? tours[tourId] : null;
  const step = def ? def.steps[stepIndex] : null;
  const isMainTour = !!tourId && tourId.startsWith("demo-");
  const isSubTour = !!tourId && tourId.startsWith("sub-");

  // Show the tooltip as soon as the tour is active so users never wait on
  // navigation or data fetches. It appears centered immediately; once the
  // anchor element renders we re-position against it.
  //
  // A requestAnimationFrame loop tracks the anchor every frame while the
  // step is active. This is more robust than scroll/resize listeners
  // because the app shell scrolls inside `.c-body` (CoreUI), and animated
  // layout shifts (collapsing sidebar, banner mounts, ag-grid virtualization)
  // also move the anchor without dispatching a scroll event. The rAF loop
  // catches all of these uniformly. Cost is negligible — getBoundingClientRect
  // is cheap and we only update React state when the rect actually changes.
  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);

    if (!active || !step) {
      setVisible(false);
      return;
    }

    setVisible(true);

    // Synchronous first paint so the tooltip doesn't flash at the previous
    // step's position before the rAF loop kicks in.
    {
      const initial = step.selector ? document.querySelector(step.selector) : null;
      setPos(getAnchorPosition(initial, step.placement));
    }

    let raf = 0;
    let lastSig = "";
    const tick = () => {
      const found = step.selector ? document.querySelector(step.selector) : null;
      const rect = found ? (found as Element).getBoundingClientRect() : null;
      // Skip React updates if neither the anchor nor its rect changed since
      // the last frame — keeps the loop cheap during idle periods.
      const sig = rect
        ? `${Math.round(rect.top)}|${Math.round(rect.left)}|${Math.round(rect.width)}|${Math.round(rect.height)}`
        : "none";
      if (sig !== lastSig) {
        lastSig = sig;
        setPos(getAnchorPosition(found, step.placement));
      }
      raf = window.requestAnimationFrame(tick);
    };
    raf = window.requestAnimationFrame(tick);

    return () => {
      window.cancelAnimationFrame(raf);
      clearInterval(pollRef.current);
    };
  }, [active, step, location.pathname, location.search]);

  if (!visible || !step || !pos) return null;

  const total = def ? def.steps.length : 0;
  const isLast = stepIndex === total - 1;

  const tooltipStyle: React.CSSProperties = pos.centered
    ? {
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        zIndex: 9999,
        width: TOOLTIP_WIDTH,
        background: "#fff",
        borderRadius: "12px",
        boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
        padding: "1.25rem"
      }
    : {
        position: "fixed",
        top: pos.top,
        left: pos.left,
        transform: pos.transform,
        zIndex: 9999,
        width: TOOLTIP_WIDTH,
        background: "#fff",
        borderRadius: "12px",
        boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
        padding: "1.25rem"
      };

  return ReactDOM.createPortal(
    <>
      {/* Backdrop highlight for the target element. Uses viewport coords +
          fixed positioning so it tracks the anchor while inner containers
          scroll (the parent effect re-renders on scroll). */}
      {!pos.centered && step.selector && (() => {
        const el = document.querySelector(step.selector);
        if (!el) return null;
        const rect = el.getBoundingClientRect();
        return (
          <div
            style={{
              position: "fixed",
              top: rect.top - 4,
              left: rect.left - 4,
              width: rect.width + 8,
              height: rect.height + 8,
              borderRadius: "8px",
              boxShadow: "0 0 0 4000px rgba(0,0,0,0.45)",
              zIndex: 9998,
              pointerEvents: "none"
            }}
          />
        );
      })()}

      <div style={tooltipStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.5rem" }}>
          <strong style={{ fontSize: "0.95rem" }}>{step.title}</strong>
          <button
            onClick={skipTour}
            style={{ background: "none", border: "none", cursor: "pointer", color: "#adb5bd", fontSize: "1rem", padding: 0, lineHeight: 1 }}
            aria-label="Skip tour"
          >
            ✕
          </button>
        </div>
        <p style={{ fontSize: "0.85rem", color: "#495057", marginBottom: "1rem", lineHeight: 1.5 }}>
          {step.body}
        </p>
        {isLast && isSandbox && (isMainTour || isSubTour) && (
          <div style={{ marginBottom: "1rem" }}>
            <div style={{ fontSize: "0.75rem", fontWeight: 600, color: "#495057", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: "0.5rem" }}>
              {isMainTour ? "Take a deeper tour" : "Explore another feature"}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.4rem" }}>
              {SUB_TOURS_MENU.filter(t => t.id !== tourId).map(t => (
                <button
                  key={t.id}
                  onClick={() => startTour(t.id)}
                  style={{
                    background: "#f8f9fa",
                    border: "1px solid #dee2e6",
                    borderRadius: "8px",
                    padding: "0.5rem 0.6rem",
                    textAlign: "left",
                    cursor: "pointer",
                    fontSize: "0.78rem",
                    lineHeight: 1.3
                  }}
                  title={t.description}
                >
                  <span style={{ marginRight: "0.35rem" }}>{t.emoji}</span>
                  <span style={{ fontWeight: 600 }}>{t.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.5rem" }}>
          <span style={{ fontSize: "0.75rem", color: "#adb5bd" }}>
            {stepIndex + 1} / {total}
          </span>
          {isLast && isSandbox ? (
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <button
                onClick={skipTour}
                style={{
                  background: "transparent",
                  color: "#6c757d",
                  border: "1px solid #dee2e6",
                  borderRadius: "6px",
                  padding: "0.35rem 0.75rem",
                  cursor: "pointer",
                  fontSize: "0.8rem"
                }}
              >
                Close
              </button>
              <a
                href="/signup"
                style={{
                  background: "#3f6ad8",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  padding: "0.35rem 1rem",
                  textDecoration: "none",
                  fontSize: "0.85rem",
                  fontWeight: 600
                }}
              >
                Sign up free →
              </a>
            </div>
          ) : (
            <button
              onClick={nextStep}
              style={{
                background: "#3f6ad8",
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                padding: "0.35rem 1rem",
                cursor: "pointer",
                fontSize: "0.85rem",
                fontWeight: 600
              }}
            >
              {isLast ? "Done" : "Next →"}
            </button>
          )}
        </div>
      </div>
    </>,
    document.body
  );
};

export default TourOverlay;
